/* screens/settings/BusinessIntegrationsTab.jsx — подвкладка «Интеграции» бизнес-ws.
   Подключение учётной системы (iiko / Syrve — ex-iiko, тот же Server API / r_keeper —
   прямое чтение базы кассы) к пространству: реквизиты доступа (секреты хранятся
   зашифрованными, не отображаются) + проверка связи + статус. Одно пространство =
   одна интеграция. Скоуп по wsId (X-Workspace-Id).
   GET/PUT/DELETE /api/integrations/, POST /api/integrations/test/.

   ФОРМА ДОСТУПОВ УПРАВЛЯЕТСЯ РЕЕСТРОМ PROVIDERS, а не разветвлениями по коду
   системы: набор полей у систем разный (см. комментарий у реестра). То же для
   валюты по умолчанию и применимости среза по заведению.

   Рендер «что даёт система» (чек-лист прав iiko ИЛИ области данных r_keeper),
   «Истории данных» и инструкции подключения — общие компоненты
   components/patterns/integrations/; здесь остаются только загрузка данных и
   поллинг. Инструкция (PosGuide) встроена прямо во вкладку между выбором
   системы и формой доступов (акцепт онбординга: инструкция живёт не только в
   онбординге, а в самом окне интеграций); успешный тест связи сигналит квесту
   онбординга событием 'onb:integration-active'. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { BackfillProgress, backfillMovementText, backfillPolling, backfillStatusText,
  backfillVisible, groupBackfillMonths }
  from '../../components/patterns/integrations/BackfillProgress.jsx';
import { PosGuide } from '../../components/patterns/integrations/PosGuide.jsx';
import { RightsChecklist } from '../../components/patterns/integrations/RightsChecklist.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, PasswordInput, TextInput } from '../../components/ui/Field.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { CURRENCIES } from '../../lib/format.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { integrationsReal } from '../../lib/adapters/integrations.js';

  const { Fragment, useEffect, useState } = React;
  const { useFakeQuery } = hooks;

  // Когда состояние склада узнали в последний раз, человеческим языком.
  // `source: 'sync'` — узнал ночной проход, `'test'` — человек нажал «Проверить»:
  // сроки у них разные, и «проверено вчера ночью» объясняет свежесть точнее, чем
  // голая дата. Некорректную дату не показываем вовсе — лучше ничего, чем
  // «Invalid Date» рядом с утверждением о работоспособности.
  function shCheckedAt(state) {
    const d = new Date(state.checked_at);
    if (Number.isNaN(d.getTime())) return null;
    const when = d.toLocaleString('ru-RU', { day: 'numeric', month: 'long',
                                             hour: '2-digit', minute: '2-digit' });
    return state.source === 'sync' ? `${when} (загрузкой данных)` : when;
  }

  const STATUS = {
    new: { tone: 'neutral', label: 'Не проверено' },
    active: { tone: 'ok', label: 'Активно' },
    error: { tone: 'bad', label: 'Ошибка' },
  };

  // Провайдеры учётных систем. Syrve — международный ребренд iiko (ex-iiko):
  // Server API общий, различаются только адреса облака и тексты. r_keeper — другая
  // система по сути: облачного API нет, Ресториум читает базу кассы напрямую.
  // Выбор — явный, «либо-либо»: одно пространство подключается ровно к одной системе.
  //
  // ФОРМУ ДОСТУПОВ СТРОИТ ЭТОТ РЕЕСТР, А НЕ JSX. У систем разные реквизиты (у
  // r_keeper вместо «адреса сервера» — адрес SQL-инстанса плюс необязательное имя
  // базы), и разъезд начинался бы с одного `if (provider === 'rkeeper')` в разметке,
  // а кончался бы формой, где каждое поле знает про каждую систему. Поэтому провайдер
  // описывает СЕБЯ: `fields` (поля доступов), `defaultCurrency`, `departments`
  // (применим ли срез по заведению). Разметка ниже перебирает `fields` и о конкретных
  // системах не знает ничего — следующий провайдер добавляется записью здесь.
  //
  // Дескриптор поля: {key — имя в payload PUT /api/integrations/; label; full —
  // на всю ширину сетки; placeholder; hint; required + requiredError; secret —
  // значение не приходит с сервера и пустым НЕ отправляется (пароль: пусто =
  // «не менять»), savedLabel/savedPlaceholder — подписи, когда секрет уже сохранён}.
  const PROVIDERS = {
    iiko: {
      label: 'iiko',
      hint: 'Российское облако iiko: сервер вида my-restoran.iiko.it',
      intro: 'Подключите облачный сервер iiko этого заведения, чтобы ассистент и аналитика '
        + 'работали с продажами и номенклатурой. Пароль хранится в зашифрованном виде '
        + 'и не отображается.',
      guideStripHint: 'как создать API-пользователя iiko',
      defaultCurrency: 'RUB',
      departments: true,
      fields: [
        { key: 'base_url', label: 'Адрес сервера', full: true, required: true,
          requiredError: 'Укажите адрес сервера',
          placeholder: 'например, my-restoran.iiko.it' },
        { key: 'login', label: 'Логин', required: true, requiredError: 'Укажите логин',
          placeholder: 'сервисный пользователь, напр. restorium_api' },
        { key: 'password', label: 'Пароль', secret: true, required: true,
          requiredError: 'Укажите пароль',
          placeholder: 'пароль сервисного пользователя',
          savedLabel: 'Пароль (пусто — не менять)', savedPlaceholder: '•••••••• сохранён' },
      ],
    },
    syrve: {
      label: 'Syrve',
      hint: 'Международный ребренд iiko (ex-iiko): сервер вида my-restaurant.syrve.online',
      intro: 'Подключите облачный сервер Syrve (ex-iiko) этого заведения, чтобы ассистент '
        + 'и аналитика работали с продажами и номенклатурой. Пароль хранится '
        + 'в зашифрованном виде и не отображается.',
      guideStripHint: 'как создать API-пользователя Syrve',
      defaultCurrency: 'AED',
      departments: true,
      fields: [
        { key: 'base_url', label: 'Адрес сервера', full: true, required: true,
          requiredError: 'Укажите адрес сервера',
          placeholder: 'например, my-restaurant.syrve.online' },
        { key: 'login', label: 'Логин', required: true, requiredError: 'Укажите логин',
          placeholder: 'сервисный пользователь, напр. restorium_api' },
        { key: 'password', label: 'Пароль', secret: true, required: true,
          requiredError: 'Укажите пароль',
          placeholder: 'пароль сервисного пользователя',
          savedLabel: 'Пароль (пусто — не менять)', savedPlaceholder: '•••••••• сохранён' },
      ],
    },
    rkeeper: {
      label: 'r_keeper',
      hint: 'Прямое чтение базы кассы: доступ к ней открывает дилер UCS',
      // Честность про себестоимость стоит ДО подключения, а не после: касса её
      // не ведёт (калькуляция живёт в StoreHouse), и узнать об этом из пустой
      // вкладки Аналитики — худший из возможных способов. Теперь у этого есть
      // продолжение: склад подключается тут же, вторым блоком полей.
      intro: 'Подключите базу кассы r_keeper этого заведения — Ресториум будет читать из неё '
        + 'продажи. Себестоимость касса не ведёт: калькуляция живёт в StoreHouse, и без '
        + 'него food cost, наценка и прибыль останутся пустыми. Доступ к StoreHouse '
        + 'открывает тот же дилер UCS и бесплатно — реквизиты второго блока '
        + 'необязательны, но именно они оживляют себестоимость. Полный перечень '
        + 'доступного появится ниже после сохранения. Пароли хранятся в зашифрованном '
        + 'виде и не отображаются.',
      guideStripHint: 'как получить доступ к базе r_keeper',
      defaultCurrency: 'RUB',
      // Срез по заведению неприменим: одна база r_keeper = один объект.
      departments: false,
      fields: [
        { key: 'base_url', label: 'Адрес SQL-инстанса', required: true,
          requiredError: 'Укажите адрес SQL-инстанса',
          placeholder: 'например, sql.example.ru:1433',
          hint: 'Хост или хост:порт из письма UCS' },
        { key: 'database', label: 'Имя базы',
          placeholder: 'необязательно',
          hint: 'Пусто — определим сами (обычно RK7)' },
        { key: 'login', label: 'Логин SQL-пользователя', required: true,
          requiredError: 'Укажите логин', placeholder: 'из письма UCS' },
        // Код объекта нужен сетям UCS, где база называется RK7_<код>. Поле
        // необязательное, но БЕЗ НЕГО самообслуживание не покрывало сети: бэкенд
        // реквизит принимал и отдавал, а вписать его человеку было некуда —
        // оставалось звать нас руками.
        { key: 'object_code', label: 'Код объекта',
          placeholder: 'необязательно',
          hint: 'Для сетей UCS, где база называется RK7_<код>' },
        { key: 'password', label: 'Пароль', secret: true, required: true,
          requiredError: 'Укажите пароль',
          placeholder: 'пароль SQL-пользователя',
          savedLabel: 'Пароль (пусто — не менять)', savedPlaceholder: '•••••••• сохранён' },

        // ── Канал до облака UCS: файл .ovpn из письма ──────────────────────
        // Облачный r_keeper пускает к базе только через VPN-туннель, конфиг
        // которого UCS присылает вместе с реквизитами. Без файла в форме
        // человек вписывал реквизиты, жал «Проверить» и получал «SQL-сервер
        // недоступен» — и шёл проверять логин, который тут ни при чём. Файл
        // читается в браузере и уходит ТЕКСТОМ в том же PUT; бэкенд принимает
        // его только через allowlist-санитайзер и хранит зашифрованным.
        { key: 'vpn_config', type: 'file', full: true,
          label: 'Файл конфигурации VPN (.ovpn) из письма UCS',
          section: 'Канал до облака UCS (r_k Cloud)',
          sectionHint: 'Для облачного r_keeper обязателен: вместе с реквизитами UCS присылает '
            + 'файл конфигурации VPN — загрузите его, и канал поднимется сам. '
            + 'Для локальной установки r_keeper файл не нужен.',
          hint: 'Файл не отображается и не скачивается обратно; при замене просто загрузите новый.' },

        // ── StoreHouse: ВТОРАЯ система того же заведения ────────────────────
        // Касса и склад у UCS — физически разные продукты со своими хостом,
        // портом и учёткой, но принадлежат ОДНОМУ заведению; разводить их по
        // двум подключениям значило бы завести пространству две интеграции там,
        // где у него один ресторан.
        //
        // ⚠️ ВСЕ ЧЕТЫРЕ ПОЛЯ НЕОБЯЗАТЕЛЬНЫ, И ЭТО ГЛАВНЫЙ ИНВАРИАНТ ФОРМЫ.
        // Склад есть не у всех, а подключение кассы не имеет права стать
        // сложнее оттого, что рядом появилась вторая система: не заполнил —
        // продажи идут, food cost в прочерках. Заполнил — появилась
        // себестоимость. Поэтому `required` тут нет ни у одного поля.
        { key: 'sh_host', label: 'Адрес сервера StoreHouse', required: false,
          section: 'Себестоимость: StoreHouse (необязательно)',
          sectionHint: 'Без этих реквизитов продажи всё равно загружаются, а food cost, '
            + 'наценка и прибыль остаются пустыми. Доступ к StoreHouse Web API открывает '
            + 'дилер UCS — по заявке и бесплатно.',
          placeholder: 'например, sh.example.ru:12205',
          hint: 'Хост или хост:порт из письма дилера' },
        { key: 'sh_port', label: 'Порт',
          placeholder: 'необязательно',
          hint: 'Пусто — 12205 или порт из адреса' },
        { key: 'sh_login', label: 'Логин StoreHouse',
          placeholder: 'пользователь StoreHouse' },
        // Предупреждение про открытый канал стоит У ПОЛЯ ПАРОЛЯ, а не в общем
        // тексте: решение «какой учёткой платить за нешифрованный канал»
        // принимается ровно в этот момент. То же самое повторит результат
        // проверки связи — тот, кто читает подсказки, и тот, кто жмёт кнопку, не
        // всегда один человек.
        { key: 'sh_password', label: 'Пароль StoreHouse', secret: true,
          savedFlag: 'has_sh_password',
          placeholder: 'пароль пользователя StoreHouse',
          hint: 'Сервис UCS работает по HTTP — соединение не шифруется. Заведите для '
            + 'Ресториума отдельного пользователя только на чтение.',
          savedLabel: 'Пароль StoreHouse (пусто — не менять)',
          savedPlaceholder: '•••••••• сохранён' },
      ],
    },
  };
  const PROVIDER_ORDER = ['iiko', 'syrve', 'rkeeper'];

  // Явный выбор системы: карточки-радио по PROVIDER_ORDER. Без подключения дефолта
  // НЕТ — пока система не выбрана, форма доступов не показывается: одна из трёх,
  // но осознанно (у систем разные реквизиты и разный объём данных).
  // locked — подключение уже существует: выбор заблокирован (смена системы только
  // через «Отключить»), карточки показывают текущую систему read-only.
  function ProviderPick({ value, onChange, locked = false }) {
    return (
      <div className="provider-pick" role="radiogroup" aria-label="Учётная система">
        {PROVIDER_ORDER.map((key) => {
          const p = PROVIDERS[key];
          const selected = value === key;
          return (
            <button type="button" key={key} role="radio" aria-checked={selected}
              className={'provider-pick__card' + (selected ? ' is-selected' : '')
                + (locked ? ' is-locked' : '')}
              disabled={locked}
              onClick={() => { if (!locked) onChange(key); }}>
              <span className="provider-pick__head">
                <span className="provider-pick__name">{p.label}</span>
                {selected && <Icon name="check-circle" size={18} className="provider-pick__check" />}
              </span>
              <span className="provider-pick__hint">{p.hint}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Что даёт подключённая система: чек-лист прав API-пользователя (iiko/Syrve) либо
  // список областей данных (r_keeper) — развилку по полю `source` держит сам общий
  // RightsChecklist (components/patterns/integrations), здесь только загрузка кэша и
  // «Проверить права» (свежая проба). onRecheck передаём всегда: у ответа
  // source='capabilities' кнопки нет вовсе — перепроверять там нечего, и решает это
  // одно место, а не два условия по разные стороны пропса. version растёт после
  // «Проверить связь» — тест на бэке заодно перепробовал права.
  // onRechecked — свежая проба прошла. Нужен блоку «История данных»: бэкенд на ней
  // же поднимает несостоявшуюся первичную загрузку, и без сигнала причина «нет
  // права» висела бы на экране до перезагрузки страницы, хотя загрузка уже идёт.
  function RightsChecklistBlock({ wsId, version, providerLabel, onRechecked }) {
    const toast = useToast();
    const q = useFakeQuery('iiko-perms-set:' + (wsId == null ? '' : wsId) + ':' + version,
      () => integrationsReal.permissions(wsId), [wsId, version]);
    const [checking, setChecking] = useState(false);
    const recheck = async () => {
      setChecking(true);
      try {
        await integrationsReal.checkPermissions(wsId);
        await q.reload();
        if (onRechecked) onRechecked();
      }
      catch (e) { toast.show((e && e.detail) || 'Не удалось проверить права', 'danger'); }
      finally { setChecking(false); }
    };
    return (
      <RightsChecklist data={q.data} onRecheck={recheck} busy={checking}
        providerLabel={providerLabel} />
    );
  }

  // Встроенная инструкция подключения (общий PosGuide). Пока подключение НЕ активно —
  // развёрнута по умолчанию (первый час пользователя: настроить iiko Office без
  // похода в онбординг); при активном подключении сжата в компактную полоску
  // «Показать инструкцию». Начальное open — от статуса на монтировании: после
  // теста, переведшего подключение в active, reload() перемонтирует форму через
  // скелетон QueryBoundary → блок сам пересоберётся уже свёрнутым.
  function GuideBlock({ provider, active }) {
    const [open, setOpen] = useState(!active);
    const pmeta = PROVIDERS[provider] || PROVIDERS.iiko;
    const label = pmeta.label;
    // Подпись свёрнутой полоски — из реестра: у r_keeper API-пользователя не
    // существует, обещать «как его создать» было бы прямой дезинформацией.
    const stripHint = pmeta.guideStripHint;
    return (
      <div className={'posguide-box' + (open ? '' : ' posguide-box--collapsed')}
        data-onb="integration-guide">
        {open ? (
          <>
            <div className="posguide-box__head">
              <h4 className="t-h5 posguide-box__title">Как подключить {label}</h4>
              {active && (
                <Button variant="ghost" size="sm" iconLeft="chevron-up"
                  onClick={() => setOpen(false)}>Скрыть</Button>
              )}
            </div>
            <PosGuide provider={provider} />
          </>
        ) : (
          <button type="button" className="posguide-strip" onClick={() => setOpen(true)}>
            <Icon name="book" size={16} />
            <span className="posguide-strip__label">
              Показать инструкцию — {stripHint}
            </span>
            <Icon name="chevron-down" size={16} />
          </button>
        )}
      </div>
    );
  }

  // Блок «История данных» активного подключения. Поллинг раз в 4 с, ПОКА бэкенд
  // отвечает active:true; финал/ошибка останавливают опрос сами, unmount чистит.
  // 404/ошибка/пустая карта без активной загрузки — блока просто нет (это фоновая
  // информация, тосты не показываем; условие видимости зеркалится здесь и в
  // BackfillProgress — см. комментарий у обёртки-якоря ниже).
  // version — внешний сигнал «переспроси»: растёт после «Проверить права». Опрос
  // к этому моменту мог остановиться (причина `blocked` приходит с active:false),
  // а проба только что подняла загрузку — без перезапуска опроса её не увидеть.
  function BackfillHistory({ wsId, version = 0 }) {
    const [data, setData] = useState(null);
    useEffect(() => {
      // Цепочка setTimeout вместо setInterval: следующий тик планируется ТОЛЬКО
      // после ответа — медленный ответ (>4 с) не накладывается на следующий тик,
      // а поздний ответ не перезапишет состояние после unmount/смены wsId (alive).
      let alive = true;
      let timer = null;
      const poll = async () => {
        const d = await integrationsReal.backfill(wsId);  // ошибки → null внутри адаптера
        if (!alive) return;
        setData(d);
        // Движение догружается дольше продаж и без спешки — его одного
        // достаточно переспрашивать реже.
        if (backfillPolling(d)) timer = setTimeout(poll, d.active === true ? 4000 : 15000);
      };
      poll();
      return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [wsId, version]);

    // Обёртка с якорем data-onb для квеста онбординга. Условие видимости — ТО ЖЕ,
    // что у BackfillProgress (одна функция, а не зеркало): пустой div-якорь
    // добавлял бы лишний gap в stack секции и давал бы оверлею нулевой
    // прямоугольник для подсветки. В него входит и причина `blocked`: она приходит
    // с active:false и пустой картой, и без неё блок исчезал бы ровно тогда, когда
    // ему есть что сказать («нет права на отчёты по продажам»).
    if (!backfillVisible(data)) return null;
    return <div data-onb="backfill-progress"><BackfillProgress data={data} /></div>;
  }

  function ConnectionForm({ data, reload, wsId }) {
    const toast = useToast();
    const confirm = useConfirm();
    const conn = data.connection;
    const hasConn = !!conn;
    const hasPassword = !!(conn && conn.has_password);

    // Явный выбор «либо-либо»: без подключения дефолта НЕТ (null) — форма доступов
    // появляется только после осознанного выбора системы.
    const [provider, setProvider] = useState(
      (conn && conn.provider && PROVIDERS[conn.provider] && conn.provider) || null);
    // Значения полей доступов — ОДНА карта по ключам дескрипторов, а не отдельный
    // useState на поле: набор полей задаёт реестр провайдера, и хук на каждое поле
    // пришлось бы менять при добавлении системы. Заполняем по ВСЕМ провайдерам
    // (ключ поля = имя в сериализаторе подключения), чтобы новая система читала
    // своё сохранённое значение без правки этого хука. Секреты стартуют пустыми
    // всегда — с сервера они не приходят вовсе (только флаг has_password).
    const [values, setValues] = useState(() => {
      const v = {};
      for (const code of PROVIDER_ORDER) {
        for (const f of PROVIDERS[code].fields) {
          if (!f.secret) v[f.key] = (conn && conn[f.key]) || '';
        }
      }
      return v;
    });
    const val = (key) => String(values[key] == null ? '' : values[key]);
    const setVal = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));
    // Валюта отображения POS-денег (только символ, без конвертаций). Пустая на
    // новом подключении = серверный дефолт по системе (iiko→₽, Syrve→AED, r_keeper→₽).
    const [currency, setCurrency] = useState((conn && conn.currency) || '');
    const meta = provider ? PROVIDERS[provider] : null;
    const fields = (meta && meta.fields) || [];
    // Срез по заведению (iiko Chain: один сервер = сеть точек). Пространство
    // подключается ровно к ОДНОЙ точке: у Chain выбор обязателен и после
    // сохранения фиксируется (смена — только «Отключить» + новое подключение).
    // Список заведений наполняет тест связи (departments_cache на бэке); у
    // обычного RMS в нём одна точка — селект не показываем вовсе.
    // Применимость среза — свойство системы из реестра (`departments`), а не
    // проверка кода провайдера в разметке: у r_keeper одна база = один объект,
    // делить нечего, и пустой селект «Заведение» там был бы обещанием выбора.
    const [departmentId, setDepartmentId] = useState((conn && conn.department_id) || '');
    const departments = (conn && conn.departments) || [];
    const deptLocked = !!(conn && conn.department_id);
    const deptShown = hasConn && !!(meta && meta.departments)
      && (departments.length > 1 || deptLocked);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [permVer, setPermVer] = useState(0);  // «Проверить связь» также пробует права
    const [backfillVer, setBackfillVer] = useState(0);  // «Проверить права» → переспросить историю
    // VPN-конфиг: текст выбранного файла (уходит в PUT один раз) и флаг «удалить
    // сохранённый». Ни то ни другое в `values` не живёт: это не текстовое поле.
    const [vpnFile, setVpnFile] = useState(null);
    const [vpnClear, setVpnClear] = useState(false);
    // Итог проверки СКЛАДА — С СЕРВЕРА, а не из состояния этого компонента.
    // Успешная проверка зовёт reload(), QueryBoundary перемонтирует форму, и
    // локальный стейт умирает вместе с ней: строка про склад исчезала ровно в
    // тот момент, когда её собирались прочитать (поймано живым прогоном — тесты
    // и сборка были зелёными). Заодно ответ переживает F5 и возвращение
    // назавтра: «почему food cost пустой» спрашивают не в секунду нажатия.
    const shResult = (conn && conn.storehouse_state
      && conn.storehouse_state.message) ? conn.storehouse_state : null;
    // Состояние VPN-канала — тот же образец, что у склада: пишут «Проверить
    // связь» и синк, показываем с датой проверки.
    const vpnResult = (conn && conn.has_vpn_config && conn.vpn_state
      && conn.vpn_state.message) ? conn.vpn_state : null;

    const validate = () => {
      const e = {};
      if (!provider) e.provider = 'Выберите систему';  // защитно: без выбора формы нет
      for (const f of fields) {
        if (!f.required) continue;
        // Секрет уже сохранён → пустое поле значит «не менять», а не «не заполнено».
        if (f.secret ? (!hasConn && !val(f.key)) : !val(f.key).trim()) {
          e[f.key] = f.requiredError;
        }
      }
      // Chain: выбор точки обязателен (данные не загружаются, пока не выбрана)
      if (deptShown && !deptLocked && !departmentId) {
        e.department_id = 'Выберите заведение — без него данные не загружаются';
      }
      setErrors(e);
      return Object.keys(e).length === 0;
    };

    const save = async () => {
      if (!validate()) return;
      // Ключ department_id уходит в PUT только при первом выборе точки (Chain)
      // и только когда адрес сервера не меняется этим же сохранением: срез
      // принадлежит серверу, при смене адреса бэк сбрасывает его сам. После
      // фиксации ключ не шлём вовсе — сменить точку нельзя (только «Отключить»).
      const serverKept = !!(conn && val('base_url').trim() === conn.base_url);
      const deptSent = deptShown && !deptLocked && serverKept;
      setSaving(true);
      let res = null;
      try {
        // Payload собирается по дескрипторам ТЕКУЩЕГО провайдера: значения полей
        // другой системы (юзер мог переключить карточку) в запрос не попадают.
        // Секрет пустым не отправляем — пустой пароль на бэке означал бы «стереть».
        const payload = { provider };
        for (const f of fields) {
          if (f.type === 'file') continue;   // файл уходит отдельно, см. ниже
          const raw = val(f.key);
          if (f.secret) { if (raw) payload[f.key] = raw; }
          else payload[f.key] = raw.trim();
        }
        // VPN-конфиг: текст файла — только если выбран новый; «удалить» — флагом.
        if (vpnFile && vpnFile.text) payload.vpn_config = vpnFile.text;
        if (vpnClear) payload.vpn_clear = true;
        if (currency) payload.currency = currency;
        if (deptSent) payload.department_id = departmentId;
        res = await integrationsReal.save(payload, wsId);
        // Секреты обратно в форму не возвращаются — обнуляем поля ввода.
        setValues((prev) => {
          const next = { ...prev };
          for (const f of fields) if (f.secret) next[f.key] = '';
          return next;
        });
        setVpnFile(null);
        setVpnClear(false);
      } catch (err) {
        toast.show((err && err.detail) || 'Не удалось сохранить подключение', 'danger');
        setSaving(false);
        return;
      }
      setSaving(false);
      if (res && res.department_set) {
        toast.show('Заведение зафиксировано — загружаем историю точки', 'muted');
      }
      // Автопроверка связи после сохранения: у нового подключения кнопки «Проверить
      // связь» до сохранения нет, и «сохранил и ушёл» оставлял подключение без
      // верификации и без первичного бэкфилла до ночного синка. Триггер — серверная
      // правда из ответа PUT: не-active (new — креды новые/изменились; error —
      // пересохранение как retry) требует проверки; active (пересохранение без
      // изменений) — тест не гоняем. ВАЖНО: до завершения test() НЕ звать reload() —
      // useFakeQuery.reload перемонтирует форму через скелетон QueryBoundary, и
      // testing-состояние гибнет (кнопки разблокируются на время пробы, возможен
      // конкурентный тест → дублирующий первичный бэкфилл). test() сам сделает
      // reload по завершении; пилюля статуса до этого момента показывает прежнее
      // значение — это осознанный компромисс на ≤20с пробы.
      const savedConn = res && res.connection;
      if (savedConn && savedConn.has_password && savedConn.status !== 'active') {
        toast.show('Подключение сохранено, проверяем связь…', 'muted');
        await test();
      } else {
        toast.show('Подключение сохранено', 'success');
        await reload();
      }
    };

    const test = async () => {
      setTesting(true);
      try {
        const res = await integrationsReal.test(wsId);
        toast.show(res.message || (res.ok ? 'Подключение успешно' : 'Не удалось подключиться'),
          res.ok ? 'success' : 'danger');
        // Результат по СКЛАДУ едет в ответе, но рисуем мы его не отсюда: бэкенд
        // кладёт его на подключение, и reload() ниже принесёт его вместе с
        // формой. Тост для склада не годится — он живёт секунды, а этот текст
        // обязан пережить чтение: в нём либо причина, по которой food cost
        // останется пустым, либо предупреждение про нешифрованный канал.
        // Успешный тест = бэкенд перевёл подключение в active — сигналим квесту
        // онбординга (оверлей слушает 'onb:integration-active'). Шлём на КАЖДЫЙ
        // успешный тест (не только первый flip): если юзер на шаге квеста
        // перепроверяет уже активное подключение, шаг всё равно должен зачесться;
        // лишние события вне шага оверлей игнорирует.
        if (res.ok) {
          try { window.dispatchEvent(new CustomEvent('onb:integration-active')); } catch { /* noop */ }
        }
        await reload();
        setPermVer((v) => v + 1);  // на тесте бэкенд заодно перепробовал права — обновим чек-лист
      } catch (err) {
        toast.show((err && err.detail) || 'Не удалось проверить подключение', 'danger');
      } finally { setTesting(false); }
    };

    const disconnect = async () => {
      const connectedMeta = (conn && PROVIDERS[conn.provider]) || meta || PROVIDERS.iiko;
      const ok = await confirm({
        title: 'Отключить ' + connectedMeta.label + '?',
        body: 'Сохранённые доступы к серверу будут удалены. Данные в самой системе '
          + connectedMeta.label + ' это не затронет.',
        confirmLabel: 'Отключить', cancelLabel: 'Отмена', tone: 'danger',
      });
      if (!ok) return;
      try {
        await integrationsReal.remove(wsId);
        toast.show('Интеграция отключена', 'muted');
        await reload();
      } catch (err) {
        toast.show('Не удалось отключить', 'danger');
      }
    };

    const st = (conn && STATUS[conn.status]) || STATUS.new;

    return (
      <section className="bill-section stack stack-16">
        <div className="bill-section__head cluster" style={{ justifyContent: 'space-between' }}>
          <h3 className="t-h4">Учётная система</h3>
          {hasConn && <Pill tone={st.tone} size="sm">{st.label}</Pill>}
        </div>
        <p className="t-body-sm text-mute">
          {hasConn
            ? 'Чтобы сменить систему, сначала отключите текущее подключение — кнопка «Отключить» внизу.'
            : 'Выберите POS-систему для подключения'}
        </p>
        <ProviderPick value={provider} onChange={setProvider} locked={hasConn} />

        {!provider && (
          <p className="t-body-sm text-mute">
            Выберите систему, чтобы указать доступы.
          </p>
        )}

        {meta && (
          <>
            {/* инструкция — между выбором системы и формой доступов: развёрнута,
                пока подключение не активно; при активном — компактная полоска */}
            <GuideBlock provider={provider} active={!!(conn && conn.status === 'active')} />

            <p className="t-body-sm text-mute">{meta.intro}</p>

            <div className="profile-grid">
              {/* Поля доступов — из дескрипторов провайдера (см. PROVIDERS выше).
                  Разметка не знает ни одного имени поля: у r_keeper здесь адрес
                  SQL-инстанса и необязательное имя базы вместо «адреса сервера». */}
              {fields.map((f) => {
                // Флаг «секрет уже сохранён» — СВОЙ у каждого пароля: у
                // r_keeper их два (касса и склад), это разные учётки в разных
                // системах, и один флаг на двоих показывал бы «сохранён» там,
                // где пароля склада ещё нет вовсе.
                const saved = !!(f.secret && (conn ? conn[f.savedFlag || 'has_password'] : false));
                const label = saved && f.savedLabel ? f.savedLabel : f.label;
                const placeholder = saved && f.savedPlaceholder
                  ? f.savedPlaceholder : f.placeholder;
                const Input = f.secret ? PasswordInput : TextInput;
                if (f.type === 'file') {
                  // Файл VPN-конфига: поле-загрузчик со своим состоянием. Сам
                  // конфиг обратно не приходит (внутри — ключ туннеля), форма
                  // знает только флаг «загружен» и состояние канала.
                  const loaded = !!(conn && conn.has_vpn_config) && !vpnClear;
                  const onPick = (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) { setVpnFile(null); return; }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setVpnFile({ name: file.name, text: String(reader.result || '') });
                      setVpnClear(false);
                    };
                    reader.readAsText(file);
                  };
                  return (
                    <Fragment key={f.key}>
                      {f.section && (
                        <div className="profile-grid__full ap-form-section">
                          <div className="t-title-xs">{f.section}</div>
                          {f.sectionHint && (
                            <p className="t-body-sm text-mute">{f.sectionHint}</p>
                          )}
                        </div>
                      )}
                      <Field label={label} error={errors[f.key]} hint={f.hint}
                        className="profile-grid__full">
                        <input type="file" accept=".ovpn,.conf,text/plain"
                          aria-label={label} onChange={onPick} />
                        <p className="t-caption text-mute">
                          {vpnFile
                            ? `Выбран файл ${vpnFile.name} — сохраните подключение.`
                            : loaded ? 'Конфиг загружен.'
                              : vpnClear ? 'Конфиг будет удалён при сохранении.'
                                : 'Файл не загружен.'}
                          {loaded && !vpnFile && (
                            <>
                              {' '}
                              <Button variant="ghost" size="sm" iconLeft="trash"
                                onClick={() => setVpnClear(true)}>Удалить конфиг</Button>
                            </>
                          )}
                        </p>
                      </Field>
                    </Fragment>
                  );
                }
                return (
                  <Fragment key={f.key}>
                    {/* Заголовок группы полей: у r_keeper их две — касса и
                        склад. Без разделителя четыре поля второй системы
                        читаются как продолжение первой, и человек ищет, куда
                        вписать пароль от кассы во второй раз. */}
                    {f.section && (
                      <div className="profile-grid__full ap-form-section">
                        <div className="t-title-xs">{f.section}</div>
                        {f.sectionHint && (
                          <p className="t-body-sm text-mute">{f.sectionHint}</p>
                        )}
                      </div>
                    )}
                    <Field label={label} error={errors[f.key]} hint={f.hint}
                      className={f.full ? 'profile-grid__full' : undefined}>
                      <Input value={val(f.key)} placeholder={placeholder}
                        invalid={!!errors[f.key]}
                        onChange={(e) => setVal(f.key, e.target.value)} />
                    </Field>
                  </Fragment>
                );
              })}
              {/* Валюта отображения: только символ у денег Аналитики, без конвертаций.
                  Server API валюту не отдаёт — дефолт по системе (из реестра), менять
                  можно всегда (не сбрасывает статус подключения). */}
              <Field label="Валюта отображения" error={errors.currency}
                hint="Символ у денег в Аналитике; суммы не конвертируются">
                <Select value={currency || meta.defaultCurrency}
                  onChange={(v) => setCurrency(v)} ariaLabel="Валюта отображения"
                  options={CURRENCIES.map((c) => ({ value: c.code, label: c.label }))} />
              </Field>
              {/* Срез по заведению — только когда сервер вернул несколько точек
                  (iiko Chain). Пространство подключается ровно к ОДНОЙ точке:
                  выбор обязателен и после сохранения фиксируется (сменить можно
                  только отключив подключение); на другую точку — отдельное
                  пространство со своим подключением. */}
              {deptShown && (
                <Field label="Заведение" className="profile-grid__full"
                  error={errors.department_id}
                  hint={deptLocked
                    ? 'Заведение зафиксировано за этим пространством. Чтобы подключить '
                      + 'другую точку, отключите подключение и подключите заново — '
                      + 'или создайте для неё отдельное пространство.'
                    : 'На сервере несколько заведений — выберите, чьи данные загружать '
                      + 'в это пространство. Пока точка не выбрана, данные не загружаются. '
                      + 'Для другой точки создайте отдельное пространство.'}>
                  <Select value={departmentId} onChange={(v) => setDepartmentId(v)}
                    ariaLabel="Заведение" disabled={deptLocked}
                    options={deptLocked
                      ? [{ value: departmentId,
                          label: (conn && conn.department_name) || 'Выбранное заведение' }]
                      : [
                        { value: '', label: '— выберите заведение —' },
                        ...departments.map((d) => ({ value: d.id, label: d.name })),
                      ]} />
                </Field>
              )}
            </div>

            {conn && conn.status === 'error' && conn.last_error && (
              <p className="t-body-sm text-mute">Последняя ошибка: {conn.last_error}</p>
            )}

            {/* Итог проверки СКЛАДА. Отдельной строкой и отдельным тоном: касса
                и склад — разные системы, и «касса работает, склад не ответил»
                обязано читаться именно так, а не как общий отказ. Ошибка склада
                не красит подключение в «Ошибка»: без склада продажи идут,
                пустой остаётся только себестоимость — и текст говорит ровно это. */}
            {vpnResult && (
              <div className={'rights-note' + (vpnResult.ok ? ' rights-note--ok' : '')}
                role="note">
                <Icon name={vpnResult.ok ? 'check-circle' : 'alert-circle'} size={16} />
                <span>
                  {vpnResult.message}
                  {vpnResult.checked_at && (
                    <span className="text-mute"> · проверено {shCheckedAt(vpnResult)}</span>
                  )}
                </span>
              </div>
            )}
            {shResult && (
              <div className={'rights-note' + (shResult.ok ? ' rights-note--ok' : '')}
                role="note">
                <Icon name={shResult.ok ? 'check-circle' : 'alert-circle'} size={16} />
                <span>
                  {shResult.ok ? 'Склад StoreHouse: ' : 'Склад StoreHouse не отвечает. '}
                  {shResult.message}
                  {!shResult.ok && ' Продажи это не затрагивает — пустой останется '
                    + 'только себестоимость (food cost, наценка, прибыль).'}
                  {/* ДАТА ОБЯЗАТЕЛЬНА. Без неё «подключение успешно» читается как
                      «работает сейчас» — и держится на экране месяцами, пока склад
                      лежит: состояние обновляют кнопка «Проверить связь» и ночной
                      проход, а не открытие страницы. Зелёная плашка без даты — то
                      же самое обещание без источника, против которого построена
                      вся эта ветка. */}
                  {shResult.checked_at && (
                    <span className="text-mute"> · проверено {shCheckedAt(shResult)}</span>
                  )}
                </span>
              </div>
            )}

            {hasConn && hasPassword && (
              <RightsChecklistBlock wsId={wsId} version={permVer}
                providerLabel={((conn && PROVIDERS[conn.provider]) || meta).label}
                onRechecked={() => setBackfillVer((v) => v + 1)} />
            )}

            {hasConn && conn.status === 'active' && (
              <BackfillHistory wsId={wsId} version={backfillVer} />
            )}

            <div className="cluster cluster-8" style={{ justifyContent: 'flex-end' }}>
              {hasConn && (
                <Button variant="ghost" size="sm" iconLeft="trash" onClick={disconnect}>Отключить</Button>
              )}
              {hasConn && hasPassword && (
                <Button variant="secondary" iconLeft="link" loading={testing} disabled={testing || saving}
                  onClick={test}>Проверить связь</Button>
              )}
              <Button variant="primary" iconLeft="check" loading={saving} disabled={saving || testing}
                onClick={save}>Сохранить</Button>
            </div>
          </>
        )}
      </section>
    );
  }

  // Read-only карточка для рядового участника: управлять подключением (креды,
  // отключение, тест) могут только владелец/админы — бэк отвечает 403
  // code='forbidden' на PUT/DELETE//test/ (закрытие гэпа этапа 1 team-access).
  // Участник видит только систему и статус — без адреса сервера и логина.
  function ConnectionReadOnly({ data }) {
    const conn = data.connection;
    const st = (conn && STATUS[conn.status]) || STATUS.new;
    const label = (conn && PROVIDERS[conn.provider] && PROVIDERS[conn.provider].label) || null;
    return (
      <section className="bill-section stack stack-16">
        <div className="bill-section__head cluster" style={{ justifyContent: 'space-between' }}>
          <h3 className="t-h4">Учётная система</h3>
          {conn && <Pill tone={st.tone} size="sm">{st.label}</Pill>}
        </div>
        <p className="t-body-sm text-mute">
          {conn
            ? `Подключено: ${label || 'учётная система'}`
              + (conn.department_name ? ` · заведение «${conn.department_name}»` : '') + '.'
            : 'Учётная система не подключена.'}
          {' '}Подключением управляют администраторы пространства.
        </p>
      </section>
    );
  }

  function Unavailable({ reason }) {
    return (
      <section className="set-card">
        <div className="set-card__head"><h3 className="t-h4">Интеграции</h3></div>
        <div className="set-card__body">
          <p className="t-body-sm text-mute">
            {reason || 'Интеграция недоступна для этого пространства.'}
          </p>
        </div>
      </section>
    );
  }

  function BusinessIntegrationsTab({ wsId }) {
    const q = useFakeQuery('biz-integration:' + (wsId == null ? '' : wsId),
      () => integrationsReal.get(wsId), [wsId]);
    // Управление подключением — только владелец/админ (зеркало серверного гейта).
    // Нет membership-данных (легаси-ответ) — не блокируем: сервер всё равно 403-нет.
    const { activeWorkspace } = useAuth();
    const m = activeWorkspace && activeWorkspace.membership;
    const canManage = !m || !!(m.is_admin || m.is_owner);
    return (
      <QueryBoundary loading={q.loading} error={q.error} onRetry={q.reload}
        skeleton={<span className="skeleton" style={{ height: 280, borderRadius: 'var(--radius-lg)', display: 'block' }} />}>
        {q.data && (q.data.available
          ? (canManage
            ? <ConnectionForm data={q.data} reload={q.reload} wsId={wsId} />
            : <ConnectionReadOnly data={q.data} />)
          : <Unavailable reason={q.data.reason} />)}
      </QueryBoundary>
    );
  }

// pure-хелперы «Истории данных» экспортированы для vitest (BusinessIntegrationsTab.test.js);
// живут в общем BackfillProgress.jsx — реэкспорт сохраняет прежнюю точку импорта.
// PROVIDERS/PROVIDER_ORDER экспортированы туда же: форма собирается из реестра, и
// проверять её без DOM можно только по нему (jsdom в проекте нет).
export { BusinessIntegrationsTab, PROVIDERS, PROVIDER_ORDER,
  backfillStatusText, backfillVisible, groupBackfillMonths, backfillMovementText,
  backfillPolling };
